import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Chip, DataTable, EmptyState, FilterRow, Icon, Modal, Pane, PaneHead,
  type TableRow,
} from '@/components/kit';
import { PeriodPills } from '../components/PeriodPills';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  clock, dayScopeLabel, dayScopeRange, doctorName, lastCompleteMonth, longDayLabel,
  monthLabel, monthName, monthRange, money, num, currentMonth, shiftMonth, type DayScope,
} from '../lib/scope';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { useAppointments, useBranches, useInvoices, useProviders, useUnsignedNotes } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { DatePicker } from '@/components/ui/DatePicker';
import { PayoutDetail } from './PayoutDetail';
import { usePayrollRun } from '@/pages/roles/accountant/lib/useAccounts';

type Scope = DayScope | 'custom';

/**
 * Clinic manager · Branch ledger — `g_ledger.dc.html`.
 *
 * EVERY VISIT AS A BUSINESS EVENT, WITH NO IDENTITIES IN IT. That is the
 * board's own closing line and it is the whole design of the table: time, type,
 * doctor, amount, and a flag when a discount was given. The invoice carries a
 * patient relation and this screen never reads it.
 *
 * A row is an INVOICE, not an appointment — the ledger is the money the day
 * produced, and a visit that was never billed is not a business event yet. The
 * bill's own time is what orders it.
 */
export function ManagerLedgerPage() {
  const narrow = useNarrowViewport();
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState<Scope>(params.get('day') ? 'custom' : 'today');
  const [customDay, setCustomDay] = useState<string>(params.get('day') ?? '');
  const [picking, setPicking] = useState(false);
  const [payoutFor, setPayoutFor] = useState<{ id: string; name: string } | null>(null);
  /* THE MONTH CHIP CARRIES A ▾, so it opens a real picker (BRIEF §4.8) — the
     board's "August ▾" is a control, not a label. */
  const [monthPicking, setMonthPicking] = useState(false);
  /* THE MONTH CHIP OPENS ON THE LAST COMPLETE MONTH, which is what the board
     draws (its ledger is dated 6 Sep and the chip reads "August ▾") and what
     every report on this surface already does. A month still being lived is a
     partial month presented as a month. */
  const [monthKey, setMonthKey] = useState(lastCompleteMonth());
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [shown, setShown] = useState(25);

  const range = useMemo(() => {
    if (scope === 'custom' && customDay) return { start: customDay, end: customDay };
    if (scope === 'custom') return monthRange(monthKey);
    if (scope === 'month') return monthRange(monthKey);
    return dayScopeRange(scope);
  }, [scope, customDay, monthKey]);

  const invoices = useInvoices(range);
  const appointments = useAppointments(range);
  const { providers } = useProviders();
  const branches = useBranches();
  /* ONE BRANCH AT A TIME, as the board's chips: Maadi | Nasr City. Each money
     row carries the branch that raised it; a bill with none predates branches
     and reads as the default site's, like Today's split. */
  const [branchPick, setBranchPick] = useState<string | null>(null);
  const activeBranch = branches.find((b) => b.id === branchPick) ?? branches[0];
  const defaultBranch = branches.find((b) => b.isDefault) ?? branches[0];
  const branchName = activeBranch?.name ?? 'This clinic';
  const branchRows = useMemo(
    () =>
      branches.length < 2
        ? invoices.page.rows
        : invoices.page.rows.filter((i) => (i.branchId ?? defaultBranch?.id) === activeBranch?.id),
    [invoices.page.rows, branches.length, defaultBranch?.id, activeBranch?.id],
  );

  /* THE PANE'S MONTH IS THE MONTH OF THE ROW YOU SELECTED, not the calendar's.
     Selecting a bill dated 20 August used to dock a card headed "SEPTEMBER
     PAYOUT" over September's visit count — a doctor's August standing read out
     of September. It is the month of the range on screen, which for a single
     day is that day's month. */
  const paneMonthKey = range.end.slice(0, 7);
  const paneMonth = useMemo(() => monthRange(paneMonthKey), [paneMonthKey]);
  /* READ EAGERLY, AND HERE IS THE TRADE. A month is four paged reads that only
     the pane wants, and deferring them until a row is selected would take this
     screen well under the API's 20-requests-a-minute ceiling. It is not worth
     it: the pane then renders in the instant between the click and the
     answer, and a pane that says "0 visits · EGP 0 collected" for a second is
     a wrong number on screen, which is worse than a busy mount. The month is
     read with the day so the pane is right the moment it opens. */
  const monthInvoices = useInvoices(paneMonth);
  const monthVisits = useAppointments(paneMonth);
  const unsigned = useUnsignedNotes();
  const payrun = usePayrollRun(paneMonthKey);

  usePageMeta({ title: 'Branch ledger' });

  /* WHO BILLED IT is the doctor of the row. The invoice's `issuedBy` is the
     person who raised the bill — at a desk that is the receptionist, so the
     doctor comes from the appointment the invoice was raised against. */
  const doctorOf = useMemo(() => {
    const byAppointment = new Map<string, string>();
    for (const a of appointments.page.rows) if (a.providerId) byAppointment.set(a.id, a.providerId);
    return byAppointment;
  }, [appointments.page.rows]);

  const typeOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of appointments.page.rows) m.set(a.id, a.appointmentType);
    return m;
  }, [appointments.page.rows]);

  const timeOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of appointments.page.rows) m.set(a.id, a.time);
    return m;
  }, [appointments.page.rows]);

  const providerById = useMemo(() => {
    const m = new Map<string, (typeof providers)[number]>();
    for (const p of providers) m.set(p.id, p);
    return m;
  }, [providers]);

  interface Event {
    id: string;
    /** The calendar day the bill belongs to — what the pane's month reads. */
    day: string;
    time: string;
    sortKey: string;
    type: string;
    discount: number;
    doctorId: string | null;
    doctor: string;
    amount: number;
    /** Money still owed on the bill, and whether anything has been paid. */
    owed: number;
    paid: number;
  }

  const events: Event[] = useMemo(() => {
    const rows = branchRows.map((inv) => {
      const apptId = inv.appointmentId ?? '';
      /* THE DOCTOR OF THE ROW. The appointment's provider first; failing that
         the person who RAISED the bill, but only when that person is one of
         the clinic's providers — at a desk `issuedBy` is the receptionist, and
         printing a receptionist under DOCTOR would be worse than a blank.
         34 of this clinic's bills have no appointment at all (walk-in billing),
         and five of the seven on a typical day: those keep an honest blank. */
      const providerId = doctorOf.get(apptId)
        ?? (inv.issuedById && providerById.has(inv.issuedById) ? inv.issuedById : null);
      const apptTime = timeOf.get(apptId) ?? '';
      /* THE TIME OF THE ROW. A booked visit's own time; for a bill raised
         without one, the moment the bill itself was raised — which IS the time
         of the business event the ledger is listing. Never a dash where a real
         timestamp exists. */
      const raised = inv.createdAt ? localTime(inv.createdAt) : '';
      const t = apptTime || raised;
      const kind = typeOf.get(apptId);
      return {
        id: inv.id,
        day: inv.date,
        time: t ? clock(t) : '—',
        sortKey: `${inv.date} ${t || '00:00:00'}`,
        type: kind === 'follow-up' ? 'Follow-up' : kind ? 'New' : 'Unbooked',
        discount: Number(inv.discountAmount || 0),
        doctorId: providerId,
        doctor: providerId ? doctorName(providerById.get(providerId)) : 'Not recorded',
        amount: Number(inv.total || 0),
        owed: Number(inv.balance || 0),
        paid: Number(inv.amountPaid || 0),
      } satisfies Event;
    });
    rows.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1));
    return rows;
  }, [branchRows, doctorOf, timeOf, typeOf, providerById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      [e.time, e.type, e.doctor, String(e.amount)].some((v) => v.toLowerCase().includes(q)));
  }, [events, query]);

  const visible = filtered.slice(0, shown);
  const selected = selectedId ? filtered.find((e) => e.id === selectedId) ?? null : null;

  const collected = branchRows.reduce((s, i) => s + Number(i.amountPaid || 0), 0);
  const unpaid = branchRows.reduce((s, i) => s + Number(i.balance || 0), 0);
  /* THE HEADLINE COUNTS THE TABLE UNDER IT. It used to count APPOINTMENTS
     while every row was an INVOICE, so "19 visits" sat over seven rows and
     "7 visits" over eleven. The ledger is bills; the count is bills. */
  const visitCount = filtered.length;

  /* The selected row's doctor, read across the whole month. */
  const doc = useMemo(() => {
    if (!selected?.doctorId) return null;
    const id = selected.doctorId;
    const booked = monthVisits.page.rows.filter((a) => a.providerId === id);
    const apptIds = new Set(booked.map((a) => a.id));
    const mv = booked.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show');
    const fu = mv.filter((a) => a.appointmentType === 'follow-up').length;
    /* FOLLOW-UPS KEPT is attendance, not mix: the share of the follow-ups that
       were BOOKED with this doctor that the patient actually came to. The mix
       (`fuPct`) is a different number and goes in the visits line above. */
    const fuBooked = booked.filter((a) => a.appointmentType === 'follow-up').length;
    const cash = monthInvoices.page.rows
      .filter((i) => i.appointmentId && apptIds.has(i.appointmentId))
      .reduce((s, i) => s + Number(i.amountPaid || 0), 0);
    return {
      id,
      name: doctorName(providerById.get(id)),
      visits: mv.length,
      newPct: mv.length ? Math.round(((mv.length - fu) / mv.length) * 100) : 0,
      fuPct: mv.length ? Math.round((fu / mv.length) * 100) : 0,
      keptPct: fuBooked ? Math.round((fu / fuBooked) * 100) : null,
      collected: cash,
      unsigned: unsigned.page.rows.filter((r) => r.providerId === id).length,
    };
  }, [selected, monthVisits.page.rows, monthInvoices.page.rows, providerById, unsigned.page.rows]);

  const docLine = doc ? payrun.data?.lines.find((l) => l.personId === doc.id) ?? null : null;

  const scopeLine =
    scope === 'custom' && customDay ? longDayLabel(customDay)
      : scope === 'month' ? monthLabel(monthKey)
        : dayScopeLabel(scope as DayScope);

  const paneSig = `${selected?.id ?? ''}:${docLine?.netMinor ?? ''}:${docLine?.status ?? ''}:${doc?.visits ?? 0}:${doc?.collected ?? 0}:${doc?.unsigned ?? 0}:${doc?.keptPct ?? '-'}`;

  const exportPeriod = () =>
    exportCsv(
      `ledger-${range.start}${range.start === range.end ? '' : `_${range.end}`}`,
      ['Time', 'Type', 'Discount', 'Doctor', 'Amount'],
      filtered.map((e) => [e.time, e.type, e.discount || '', e.doctor, e.amount]),
    );
  const crumb = usePhoneCrumb('Home', 'Branch ledger');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="today" onClose={close} />,
      title: 'Branch ledger',
      sub: 'every visit as a business event · no identities',
      search: 'Search a day, a doctor, an amount…',
      searchValue: query,
      onSearchChange: setQuery,
      /* Phone: the board's header ends in a live chip and Export sits under
         the list; the desktop keeps Export as its one primary. */
      ...(narrow
        ? { phoneTrailing: scope === 'today' ? <Chip kind="success" small>● live</Chip> : undefined }
        : { primary: { label: 'Export', onClick: exportPeriod } }),
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The doctor of the selected row"
              title={`${selected.doctor} — detail`}
              sub={`${branchName} · ${doc ? monthLabel(paneMonthKey) : 'no doctor on this bill'}`}
            />
          }
          foot={
            <button type="button" className="btn btn-outline" style={{ flex: 1 }}
              disabled={!doc}
              onClick={() => doc && setPayoutFor({ id: doc.id, name: selected.doctor })}>
              Payout detail ›
            </button>
          }
        >
          {doc ? (
            <>
              <Card>
                <p className="t-ovl mut">
                  {paneMonthKey === currentMonth() ? 'This month' : monthName(paneMonthKey)}
                </p>
                <p className="t-sec sec" style={{ marginTop: 4 }}>
                  {doc.visits} visits · {doc.newPct} : {doc.fuPct} new/follow-up · {money(doc.collected)} collected
                </p>
                <p className="t-cap mut">
                  collected is the base a percentage would be computed on — cash collected plus
                  claims PAID, never billed
                </p>
              </Card>
              <Card>
                <p className="t-ovl mut">Discipline</p>
                {/* Unsigned, and follow-ups kept (attended ÷ booked, for this
                    doctor, this month). */}
                <p className={`t-sec ${doc.unsigned ? 'amb' : 'sec'}`} style={{ marginTop: 4 }}>
                  {doc.unsigned} unsigned{doc.unsigned ? '' : ' ✓'}
                  <span className="mut"> · follow-ups kept{' '}
                    {doc.keptPct === null ? 'no follow-ups booked' : `${doc.keptPct}%`}</span>
                </p>
              </Card>
              <Card>
                <p className="t-ovl mut">{monthName(paneMonthKey)} payout</p>
                <p className="t-sec sec" style={{ marginTop: 4 }}>
                  {docLine
                    ? `gross ${num(docLine.grossMinor / 100)} · deductions −${num(docLine.deductionsMinor / 100)} · net ${num(docLine.netMinor / 100)}`
                    : `${money(doc.collected)} collected is the base · the share is set in Money`}
                </p>
              </Card>
              <Card kind="accent">
                <p className="t-sec brand">
                  Counts and money, never a patient list. The payout figures are the accountant’s
                  run, read here — the manager approves it, she prepares it.
                </p>
              </Card>
              {docLine?.status === 'settled' ? (
                <p className="t-sec sec" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 4px' }}>
                  <span className="grow">
                    settled ✓{docLine.settledAt ? ` · ${new Date(docLine.settledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                  </span>
                  <Chip kind="success" small>settled ✓</Chip>
                </p>
              ) : null}
            </>
          ) : (
            <Card>
              <p className="t-ovl mut">No doctor on this bill</p>
              <p className="t-sec mut" style={{ marginTop: 4 }}>
                This bill was raised without an appointment behind it, so there is no doctor to
                extend into. Time, type and amount are all the ledger knows about it.
              </p>
            </Card>
          )}
        </Pane>
      ) : undefined,
    },
    [query, selectedId, paneSig, branchName, scope, customDay, monthKey, filtered.length,
     range.start, range.end, paneMonthKey, narrow],
  );

  const rows: TableRow[] = visible.map((e) => ({
    id: e.id,
    cells: {
      time: <div><p className="t-strong">{e.time}</p></div>,
      /* ONE LINE, like every row on the board. With the pane docked the chip
         wrapped onto a second line and made a discounted row 58px against 52
         for the rest. */
      type: (
        <span className="mut cm-typecell">
          <span className="cm-typelabel">{e.type}{e.discount > 0 ? ' · ' : ''}</span>
          {e.discount > 0 ? <Chip kind="warn" small>discount −{num(e.discount)}</Chip> : null}
        </span>
      ),
      /* One line, whatever the clinic called its doctors — an 18% column and
         a long first name wrap the row to twice the board's height. */
      doctor: (
        <span className="mut" style={{ display: 'block', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.doctor}</span>
      ),
      amount: <span className="w6 num">{num(e.amount)}</span>,
    },
  }));

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        {narrow ? <p className="t-heading" style={{ marginTop: 14 }}>{branchName}</p> : null}
        <FilterRow>
          {narrow ? null : (
            <>
              {branches.length > 1 ? (
                branches.map((b) => (
                  <button key={b.id ?? 'clinic'} type="button" aria-pressed={b.id === activeBranch?.id}
                    className={`chip chip-sm ${b.id === activeBranch?.id ? 'chip-brand w6' : 'chip-outline'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setBranchPick(b.id); setShown(25); setSelectedId(null); }}>
                    {b.name}
                  </button>
                ))
              ) : (
                <Chip kind="brand" small className="w6">{branchName}</Chip>
              )}
              <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
            </>
          )}
          <PeriodPills
            label="Period"
            active={scope}
            onSelect={(k) => {
              if (k === 'custom') { setPicking(true); return; }
              if (k === 'month') { setMonthPicking(true); return; }
              setScope(k as Scope);
              setShown(25);
              setSelectedId(null);
            }}
            items={[
              { key: 'today', label: 'Today' },
              { key: 'week', label: narrow ? 'Week' : 'This week' },
              { key: 'month', label: narrow ? 'Month' : `${monthLabel(monthKey).split(' ')[0]} ▾`, opens: true },
              ...(narrow ? [] : [{ key: 'custom', label: 'Custom', opens: true }]),
            ]}
          />
        </FilterRow>

        {/* THE NOUN AND THE COUNT ARE THE SAME THING. The board says "21
            visits" over 21 rows because on the board every visit is billed;
            here five of a typical day's seven bills have no appointment behind
            them at all, so "visits" would both miscount the table and
            contradict Today's own visit figure for the same day. The table is
            bills; the headline says bills. */}
        {narrow ? (
          <>
            <div style={{ display: 'flex', gap: 10, margin: '12px 0' }}>
              <Card kind="accent" style={{ flex: 1 }}>
                <p className="t-ovl mut">Collected</p>
                <p className="t-title num">{money(collected)}</p>
              </Card>
              <Card kind="accent" style={{ flex: 1 }}>
                <p className="t-ovl mut">Bills</p>
                <p className="t-title num">{visitCount}</p>
                <p className="t-cap mut">
                  {filtered.filter((e) => e.type === 'New').length} new ·{' '}
                  {filtered.filter((e) => e.type === 'Follow-up').length} f/u
                  {unpaid > 0 ? ` · ${num(unpaid)} unpaid` : ''}
                </p>
              </Card>
            </div>
            <p className="t-sec mut" style={{ margin: '0 0 8px' }}>Transactions</p>
          </>
        ) : null}
        {narrow ? null : (
          <p className="t-heading mb12">
            {branchName} · {scopeLine} — {visitCount} bill{visitCount === 1 ? '' : 's'} ·{' '}
            {money(collected)} collected · {num(unpaid)} unpaid
          </p>
        )}

        {narrow ? (
          <div className="cm-ledger-rows" style={{ display: 'grid', gap: 8 }}>
            {visible.length === 0 ? (
              <EmptyState
                title="No business events in this period"
                sub="A bill raised against a visit lands here as a line — its time, its type, its doctor and its amount."
              />
            ) : visible.map((e) => {
              const unpaidRow = e.owed > 0 && e.paid === 0;
              return (
                <button
                  key={e.id}
                  type="button"
                  className="card card-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
                    padding: '14px 14px', boxSizing: 'border-box', margin: 0, ...(e.owed > 0 ? { borderColor: 'var(--warning-600)' } : null),
                  }}
                  onClick={() => setSelectedId((cur) => (cur === e.id ? null : e.id))}
                >
                  <span className="t-sec mut num" style={{ flex: '0 0 auto' }}>{e.time}</span>
                  <Chip kind={e.type === 'New' ? 'soft' : 'warn'} small>
                    {e.type === 'New' ? 'new' : e.type === 'Follow-up' ? 'f/u' : '—'}
                  </Chip>
                  <span className="t-sec grow" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.doctor}
                  </span>
                  {e.discount > 0 ? <Chip kind="warn" small>−{num(e.discount)}</Chip> : null}
                  {unpaidRow
                    ? <span className="w6 amb">unpaid</span>
                    : <span className="w6 num" style={{ flex: '0 0 auto' }}>EGP {num(e.amount)}</span>}
                </button>
              );
            })}
            {shown < filtered.length ? (
              <button type="button" className="btn btn-outline" onClick={() => setShown((n) => n + 25)}>Load more</button>
            ) : null}
          </div>
        ) : (
        <DataTable
          caption="Ledger"
          /* The declared widths are the board's; `table-layout: auto` treated
             them as suggestions and redistributed them around the widest cell,
             which is how TYPE and DOCTOR ended up 8–9px left of the canvas. */
          className="cm-fixed-cols"
          columns={[
            { key: 'time', label: 'Time', width: 46 },
            { key: 'type', label: 'Type', width: 24 },
            { key: 'doctor', label: 'Doctor', width: 18 },
            { key: 'amount', label: 'Amount', width: 12 },
          ]}
          rows={rows}
          total={filtered.length}
          onLoadMore={shown < filtered.length ? () => setShown((n) => n + 25) : undefined}
          selectedId={selectedId}
          onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
          empty={
            <EmptyState
              title="No business events in this period"
              sub="A bill raised against a visit lands here as a line — its time, its type, its doctor and its amount."
            />
          }
        />
        )}

        {narrow ? (
          <>
            <button type="button" className="btn btn-outline"
              style={{ width: '100%', marginTop: 14, borderRadius: 999, height: 48 }} onClick={exportPeriod}>
              <Icon name="download" /> Export this period — CSV
            </button>
            <p className="t-sec mut" style={{ marginTop: 12, textAlign: 'center' }}>
              Time · type · doctor · amount. Nothing else.
            </p>
          </>
        ) : (
          <p className="t-sec mut" style={{ marginTop: 12 }}>
            Time · type · doctor · amount, discount-flagged. Nothing else — the ledger is every
            visit as a business event, with no identities in it.
          </p>
        )}
      </div>

      <Modal
        open={monthPicking}
        onClose={() => setMonthPicking(false)}
        title="A whole month"
        sub="every business event of the month, newest first"
        width={400}
        foot={<button type="button" className="btn btn-outline" onClick={() => setMonthPicking(false)}>Close</button>}
      >
        <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0' }}>
          {Array.from({ length: 6 }, (_, i) => shiftMonth(currentMonth(), -i)).map((m) => (
            <button key={m} type="button" className="mi"
              onClick={() => {
                setMonthKey(m); setScope('month'); setMonthPicking(false);
                setCustomDay(''); setShown(25); setSelectedId(null);
              }}>
              <span>{monthLabel(m)}</span>
              {scope === 'month' && monthKey === m ? <span className="t-cap brand w6">showing</span> : null}
            </button>
          ))}
        </div>
      </Modal>

      {payoutFor ? (
        <PayoutDetail
          open
          onClose={() => setPayoutFor(null)}
          doctorId={payoutFor.id}
          doctorName={payoutFor.name}
          period={paneMonthKey}
          periodLabel={monthLabel(paneMonthKey)}
        />
      ) : null}

      <Modal
        open={picking}
        onClose={() => setPicking(false)}
        title="A custom day"
        sub="the ledger is read one day at a time"
        width={420}
        foot={<>
          <button type="button" className="btn btn-outline" onClick={() => setPicking(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!customDay}
            onClick={() => setPicking(false)}>Show that day</button>
        </>}
      >
        <div style={{ paddingTop: 12 }}>
          {/* The product's own picker, not the browser's: `<input type="date">`
              renders its value in the BROWSER's locale — "08/20/2026" on a
              surface that writes "20 Aug 2026" on every other line, and
              genuinely ambiguous for a clinic in Cairo. */}
          <p className="t-cap mut" style={{ marginBottom: 6 }}>Pick a day</p>
          <DatePicker
            className="kit-daypicker"
            value={customDay}
            max={new Date().toISOString().slice(0, 10)}
            placeholder="Choose a day"
            ariaLabel="The day to read the ledger for"
            onChange={(v) => {
              setCustomDay(v);
              setScope('custom');
              setShown(25);
              setSelectedId(null);
              const next = new URLSearchParams(params);
              if (v) next.set('day', v); else next.delete('day');
              setParams(next, { replace: true });
            }}
          />
        </div>
      </Modal>
    </div>
  );
}

/** `HH:MM:SS` in the reader's own timezone, from an instant the API sent. */
function localTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
